import { Modal } from "react-bootstrap";
import { categoryTitle } from "./menuTreeUtils";

/**
 * Per-menu display options for one category:
 * show title · show in summary · optional summary jump target.
 *
 * Each option is a card: icon, title, one-line help, salmon switch.
 */
const OptionCard = ({ id, icon, label, help, checked, disabled, onChange, t }) => (
  <label htmlFor={id} className={`menu-settings__card ${checked ? "is-on" : ""}`}>
    <span className="menu-settings__icon" aria-hidden>
      <i className={`fas ${icon}`} />
    </span>
    <span className="menu-settings__text">
      <span className="menu-settings__label">{label}</span>
      <span className="menu-settings__help">{help}</span>
    </span>
    <span className="menu-settings__state">{checked ? t("menuTreeOn") : t("menuTreeOff")}</span>
    <input
      id={id}
      type="checkbox"
      className="menu-picker__switch"
      role="switch"
      checked={checked}
      aria-checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
  </label>
);

const CategorySettingsModal = ({
  show,
  onHide,
  onCancel,
  category,
  busy,
  onTitleVisibleChange,
  onSummaryVisibleChange,
  onSummaryTargetChange,
  summaryTargetCategoryOptions,
  summaryTargetSubcategoryOptionsByCategory,
  t,
}) => {
  if (!category) return null;

  const categoryId = String(category._id);
  const titleVisible = category.menuHideCategoryTitle !== true;
  const summaryVisible = category.menuHideFromSummary !== true;
  const targetCategoryId = category.menuSummaryTargetCategoryId || "";
  const targetSubCategoryId = category.menuSummaryTargetSubCategoryId || "";
  const subOptions =
    targetCategoryId && summaryTargetSubcategoryOptionsByCategory[targetCategoryId]
      ? summaryTargetSubcategoryOptionsByCategory[targetCategoryId]
      : [];
  const hasTarget = Boolean(targetCategoryId);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      enforceFocus={false}
      restoreFocus
      className="fade menu-picker"
      dialogClassName="menu-picker__dialog"
      contentClassName="menu-picker__content"
    >
      <Modal.Body className="menu-picker__body menu-settings">
        <div className="menu-settings__header">
          <span className="menu-settings__folder" aria-hidden>
            <i className="fas fa-folder" />
          </span>
          <div className="menu-settings__heading">
            <h3 className="menu-picker__title mb-0">{categoryTitle(category)}</h3>
            <p className="menu-picker__subtitle mb-0">{t("menuTreeSettingsSubtitle")}</p>
          </div>
          {busy && (
            <span
              className="spinner-border spinner-border-sm menu-settings__spinner"
              role="status"
              aria-label={t("loading")}
            />
          )}
        </div>

        <OptionCard
          id={`menu-tree-cat-title-${categoryId}`}
          icon="fa-heading"
          label={t("menuTreeShowTitle")}
          help={t("menuTreeShowTitleHelp")}
          checked={titleVisible}
          disabled={busy}
          onChange={(v) => onTitleVisibleChange(categoryId, v)}
          t={t}
        />

        <OptionCard
          id={`menu-tree-cat-summary-${categoryId}`}
          icon="fa-list-ul"
          label={t("menuTreeShowInSummary")}
          help={t("menuTreeShowInSummaryHelp")}
          checked={summaryVisible}
          disabled={busy}
          onChange={(v) => onSummaryVisibleChange(categoryId, v)}
          t={t}
        />

        <div className={`menu-settings__card menu-settings__card--block ${hasTarget ? "is-on" : ""}`}>
          <div className="menu-settings__card-head">
            <span className="menu-settings__icon" aria-hidden>
              <i className="fas fa-location-arrow" />
            </span>
            <span className="menu-settings__text">
              <span className="menu-settings__label">{t("menuTreeJumpTarget")}</span>
              <span className="menu-settings__help">{t("menuTreeJumpTargetHelp")}</span>
            </span>
            {hasTarget && (
              <button
                type="button"
                className="btn menu-settings__reset"
                disabled={busy}
                onClick={() => onSummaryTargetChange(categoryId, null, null)}
              >
                <i className="fas fa-undo-alt" aria-hidden /> {t("menuTreeJumpTargetReset")}
              </button>
            )}
          </div>

          <div className="menu-settings__selects">
            <label className="menu-settings__select">
              <span className="menu-settings__select-label">{t("menuTreeJumpTargetCategory")}</span>
              <span className="menu-settings__select-wrap">
                <select
                  value={targetCategoryId}
                  disabled={busy}
                  onChange={(e) => onSummaryTargetChange(categoryId, e.target.value || null, null)}
                >
                  <option value="">{t("menuTreeJumpTargetNone")}</option>
                  {summaryTargetCategoryOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <i className="fas fa-chevron-down" aria-hidden />
              </span>
            </label>

            <label className="menu-settings__select">
              <span className="menu-settings__select-label">{t("menuTreeJumpTargetSubcategory")}</span>
              <span className="menu-settings__select-wrap">
                <select
                  value={targetSubCategoryId}
                  disabled={busy || !hasTarget}
                  onChange={(e) =>
                    onSummaryTargetChange(categoryId, targetCategoryId || null, e.target.value || null)
                  }
                >
                  <option value="">{t("menuTreeJumpTargetCategoryStart")}</option>
                  {subOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <i className="fas fa-chevron-down" aria-hidden />
              </span>
            </label>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="menu-picker__footer">
        <button
          type="button"
          className="btn menu-picker__btn menu-picker__btn--cancel"
          onClick={onCancel || onHide}
          disabled={busy}
        >
          {t("cancel")}
        </button>
        <button
          type="button"
          className="btn menu-picker__btn menu-picker__btn--add"
          onClick={onHide}
          disabled={busy}
        >
          {t("menuTreeDone")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CategorySettingsModal;
